import { styled } from "@mui/material/styles";
import FormHelperText from "@mui/material/FormHelperText";
import { filterStyledProps } from "lib/helpers/filterStyledProps";

export const LabelGroupWrapper = styled("div")({ display: "flex", flexDirection: "row" });

export const FormLabel = styled("div")(({ theme }) => ({
  marginBottom: 8,
  color: theme.semantic.cTextMuted,
  fontFamily: "Barlow",
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
}));

export const EditorFrame = styled("div", { shouldForwardProp: filterStyledProps(["error"]) })<{ error?: boolean }>(
  ({ theme, error }) => ({
    overflow: "hidden",
    border: `1px solid ${error ? theme.colors.danger : theme.semantic.cInputBorder}`,
    borderRadius: 9,
    background: theme.semantic.cBtnOutlineBg,
    "&:focus-within": { borderColor: error ? theme.colors.danger : theme.semantic.cAccent },
    "& .ql-container.ql-snow": { border: "none", fontFamily: "Barlow, sans-serif", fontSize: 13 },
    "& .ql-editor": {
      minHeight: 150,
      padding: 12,
      color: theme.semantic.cTextBody,
      lineHeight: 1.6,
      "& p": { margin: 0 },
      "& ul, & ol": { margin: 0, paddingLeft: "1.35em" },
      "& li": { margin: 0, paddingLeft: 0 },
      "& a": { color: theme.semantic.cAccent, textDecoration: "underline" },
    },
    "& .ql-editor.ql-blank::before": { fontStyle: "normal", color: theme.semantic.cTextFaint, left: 12 },
  }),
);

export const Toolbar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 2,
  padding: "5px 6px",
  borderBottom: `1px solid ${theme.semantic.cBorderSoft}`,
  background: theme.semantic.cTableHeadBg,
}));

export const ToolDivider = styled("span")(({ theme }) => ({
  width: 1,
  height: 16,
  margin: "0 4px",
  background: theme.semantic.cBorderSoft,
}));

export const ToolSpacer = styled("span")({ flex: 1 });

export const ToolButton = styled("button", { shouldForwardProp: filterStyledProps(["active"]) })<{ active?: boolean }>(
  ({ theme, active }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    minWidth: 28,
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 5,
    background: active ? theme.semantic.cChipActiveBg : "transparent",
    color: active ? theme.semantic.cAccent : theme.semantic.cTextMuted,
    fontFamily: "Barlow, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": { background: theme.semantic.cChipHoverBg },
  }),
);

export const LinkToolButton = styled(ToolButton)(({ theme }) => ({
  color: theme.semantic.cAccent,
  fontSize: 11.5,
  fontWeight: 700,
}));

export const ClearToolButton = styled(ToolButton)(({ theme }) => ({
  color: theme.semantic.cTextFaint,
  fontSize: 11.5,
}));

export const LinkRow = styled("div")(({ theme }) => ({
  padding: "8px 9px",
  borderBottom: `1px solid ${theme.semantic.cBorderSoft}`,
  background: theme.semantic.cChipBg,
}));

export const LinkFields = styled("div")({ display: "flex", alignItems: "center", gap: 6 });

export const LinkInput = styled("input")(({ theme }) => ({
  boxSizing: "border-box",
  height: 32,
  minWidth: 0,
  padding: "0 10px",
  border: `1px solid ${theme.semantic.cInputBorder}`,
  borderRadius: 7,
  background: theme.semantic.cBtnOutlineBg,
  color: theme.semantic.cTextStrong,
  fontFamily: "Barlow, sans-serif",
  fontSize: 12,
}));

export const LinkTextInput = styled(LinkInput)({ width: 150 });

export const LinkUrlInput = styled(LinkInput)({ flex: 1 });

export const LinkInsertButton = styled("button")(({ theme }) => ({
  flexShrink: 0,
  height: 32,
  padding: "0 12px",
  border: "none",
  borderRadius: 7,
  background: theme.semantic.cBtnPrimaryBg,
  color: "#fff",
  fontFamily: "Barlow, sans-serif",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  "&:disabled": { opacity: 0.5, cursor: "default" },
}));

export const LinkCancelButton = styled("button")(({ theme }) => ({
  flexShrink: 0,
  height: 32,
  padding: "0 10px",
  border: `1px solid ${theme.semantic.cBtnSubtleBorder}`,
  borderRadius: 7,
  background: theme.semantic.cBtnOutlineBg,
  color: theme.semantic.cTextMuted,
  fontFamily: "Barlow, sans-serif",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
}));

export const LinkError = styled("div")(({ theme }) => ({
  marginTop: 6,
  color: theme.colors.danger,
  fontSize: 11.5,
}));

export const HelperText = styled(FormHelperText)(({ theme }) => ({
  marginLeft: 0,
  "&.Mui-error": { color: theme.colors.danger },
}));
